import { useState } from 'react';

import { useAuth } from '@opendoor/auth-fe/context';
import { COSMOS_URL } from 'components/globals';
import {
  determinePropertyActionability,
  TOUR_ACTION_TEXT_BUY_DIRECT,
  TOUR_ACTION_TEXT_NON_BUY_DIRECT,
} from 'components/marketplace/helpers/propertyActionability';
import type { PropertyActionabilityInput } from 'components/marketplace/helpers/propertyActionability';
import { useTrackProperty } from 'components/marketplace/helpers/useTrackProperty';
import type { MarketplaceProperty } from 'components/marketplace/property/types';
import { useObservability } from 'helpers/observability';

interface ActionConfig {
  text: string;
  onClick: () => void;
  href?: string;
  target?: string;
  variant: 'primary' | 'secondary' | 'secondaryInverse';
  disabled?: boolean;
  analyticsName: string;
}

interface UseTouringOptions {
  placement: 'footer' | 'lightbox' | 'main';
}

type TouringProperty = PropertyActionabilityInput & Pick<MarketplaceProperty, 'addressToken'>;

export const useTouring = (property: TouringProperty, options: UseTouringOptions) => {
  const { placement } = options;
  const { trackProperty } = useTrackProperty();
  const { trackJourneyEvent } = useObservability();
  const { authentication } = useAuth();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSelfTourDialogOpen, setIsSelfTourDialogOpen] = useState(false);
  const [isBuyDirectContextOpen, setIsBuyDirectContextOpen] = useState(false);

  const { isActionable, tourKind, showBuyDirect, disableBuyDirect } =
    determinePropertyActionability(property);

  if (!isActionable || !tourKind) {
    return null;
  }

  const buyDirectPath = `/checkout/${property.addressToken}`;
  const buyDirectUrl = `${COSMOS_URL}${buyDirectPath}`;

  // RESALE-37: guests get the context screen before the login wall. Anything
  // other than a settled session counts as a guest, so a slow `UserSession`
  // cannot drop someone into `/login` with no explanation.
  const isSignedIn = authentication.state === 'authenticated';

  // Analytics naming differs by placement:
  // | Placement      | Analytics Name Pattern                              |
  // |----------------|-----------------------------------------------------|
  // | main           | cosmos-marketplace-{action}-cta                     |
  // | footer/lightbox| cosmos-marketplace-pdp-{placement}-{action}-button  |
  const isMainPlacement = placement === 'main';
  const trackPropertySource = isMainPlacement
    ? 'marketplace-pdp'
    : `marketplace-pdp-${placement}-cta`;
  const journeyEventPlacement = isMainPlacement ? 'above-the-fold' : placement;
  const analyticsNamePrefix = isMainPlacement
    ? 'cosmos-marketplace'
    : `cosmos-marketplace-pdp-${placement}`;
  const analyticsNameSuffix = isMainPlacement ? 'cta' : 'button';

  const handleSelfTour = () => {
    trackProperty({ featureName: 'property-self-tour', label: trackPropertySource });
    trackJourneyEvent('marketplaceProperty', 'tourBookingStart', {
      addressUuid: property.addressToken,
      placement: journeyEventPlacement,
      source: 'self-tour',
    });
    setIsSelfTourDialogOpen(true);
  };

  const handleBuyDirect = () => {
    trackProperty({ featureName: 'property-buy-direct', label: trackPropertySource });
    trackJourneyEvent(
      'marketplaceProperty',
      'offerStart',
      {
        addressUuid: property.addressToken,
        source: journeyEventPlacement,
      },
      { flush: true },
    );

    if (!isSignedIn) {
      setIsBuyDirectContextOpen(true);
    }
  };

  // A link, not an imperative navigate, so Continue keeps middle-click and
  // keyboard behavior. `/login` forwards an already-signed-in customer to
  // `redirectTo` itself, and keeping `redirectTo` a path means nothing here can
  // hand it an off-site destination.
  const buyDirectContextHref = `/login?redirectTo=${encodeURIComponent(buyDirectPath)}`;

  const handleTourWithAgent = () => {
    trackProperty({ featureName: 'property-tour-with-agent', label: trackPropertySource });
    trackJourneyEvent('marketplaceProperty', 'tourBookingStart', {
      addressUuid: property.addressToken,
      placement: journeyEventPlacement,
      source: 'tour-with-agent',
    });
    setIsDialogOpen(true);
  };

  const closeDialog = () => setIsDialogOpen(false);
  const closeSelfTourDialog = () => setIsSelfTourDialogOpen(false);
  const closeBuyDirectContext = () => setIsBuyDirectContextOpen(false);

  // Determine primary and secondary actions based on property state
  let primaryAction: ActionConfig;
  let secondaryAction: ActionConfig | null = null;

  if (showBuyDirect) {
    // BuyDirect eligible properties show Buy button as primary
    primaryAction = {
      text: disableBuyDirect ? 'Offer pending' : 'Buy this home',
      onClick: handleBuyDirect,
      // atoms `Button` renders an anchor whenever `href` is set, so the guest
      // variant has to omit it to stay a dialog trigger.
      ...(isSignedIn ? { href: buyDirectUrl, target: '_blank' } : {}),
      variant: 'primary',
      disabled: disableBuyDirect,
      analyticsName: `${analyticsNamePrefix}-buy-direct-${analyticsNameSuffix}`,
    };

    // Show tour as secondary action based on tour kind
    if (tourKind === 'selfTour') {
      secondaryAction = {
        text: TOUR_ACTION_TEXT_BUY_DIRECT.selfTour,
        onClick: handleSelfTour,
        variant: placement === 'lightbox' ? 'secondaryInverse' : 'secondary',
        analyticsName: `${analyticsNamePrefix}-self-tour-${analyticsNameSuffix}`,
      };
    } else if (tourKind === 'tourWithAgent') {
      secondaryAction = {
        text: TOUR_ACTION_TEXT_BUY_DIRECT.tourWithAgent,
        onClick: handleTourWithAgent,
        variant: placement === 'lightbox' ? 'secondaryInverse' : 'secondary',
        analyticsName: `${analyticsNamePrefix}-tour-with-agent-${analyticsNameSuffix}`,
      };
    }
  } else if (tourKind === 'selfTour') {
    primaryAction = {
      text: TOUR_ACTION_TEXT_NON_BUY_DIRECT.selfTour,
      onClick: handleSelfTour,
      variant: placement === 'lightbox' ? 'secondaryInverse' : 'primary',
      analyticsName: `${analyticsNamePrefix}-self-tour-${analyticsNameSuffix}`,
    };
  } else {
    primaryAction = {
      text: TOUR_ACTION_TEXT_NON_BUY_DIRECT.tourWithAgent,
      onClick: handleTourWithAgent,
      variant: placement === 'lightbox' ? 'secondaryInverse' : 'primary',
      analyticsName: `${analyticsNamePrefix}-tour-with-agent-${analyticsNameSuffix}`,
    };
  }

  return {
    primaryAction,
    secondaryAction,
    tourKind,
    showBuyDirect,
    isDialogOpen,
    closeDialog,
    isSelfTourDialogOpen,
    closeSelfTourDialog,
    isBuyDirectContextOpen,
    closeBuyDirectContext,
    buyDirectContextHref,
  };
};
